import * as THREE from "three";
import { CheckOverObstArr, GetClickObj, GetVerPosArr } from "./Common";
import { defaultObsW, GetIdStr } from "./Loader";
import { GetObstTypeGeo, SetObstPosition, SetOutsideObstPos, SetTreeSize } from "./ObstructionControl";
import { CreateModule, SetVirModulePos } from "./ModuleControl";
import { SetMeshOBB } from "./MeshControl";
import { MODULE_GRAPH, siteCalculationState } from "../../../../../services/siteCalculationState";
import { CheckIsTreeCollidingWithRoof } from "./TransControl";
import { removeSelectedTree } from "../../../../../helpers/storeHelpers";
import { Viewer } from "../Viewer-Factory/Viewer";
import { XZCoordinates, RoofMesh, ObstructionMesh } from '../../../../../@types/types';
import { EditAction } from "../../../../../@types/enums";


const topCamera = new THREE.PerspectiveCamera(75, 1.5, 0.01, 100);
const topMouse = new THREE.Vector2();
const topRaycaster = new THREE.Raycaster();
topCamera.position.set(0, 70, 0.4);

export function GetRoofPos(posOld: XZCoordinates, roofArr: RoofMesh[], cSize: { w: number, h: number }) {
    topCamera.aspect = cSize.w / cSize.h;
    topCamera.lookAt(0, 0, 0);
    const ch = 54.3, cw = ch * topCamera.aspect;
    topMouse.x = posOld.x / cw;
    topMouse.y = -posOld.z / ch;
    topRaycaster.setFromCamera(topMouse, topCamera);
    const interInfo = topRaycaster.intersectObjects(roofArr)[0];
    return interInfo;
}

export function SetVirMeshPos(self: Viewer, e: MouseEvent | TouchEvent) {
    const { selectType, mapCustom, roofMeshArr, camera, cSize, virObs, virTree }: any = self;
    if (selectType === 'module') {
        SetVirModulePos(self, e);
        return;
    }
    const intMeshArr = selectType === 'tree' ? [mapCustom] : [...roofMeshArr, mapCustom];
    const interInfo = GetClickObj(e, intMeshArr, camera, cSize);
    if (!interInfo) return;
    let isObstructionOnRoof = false;
    if (interInfo.object.name.includes('roof')) {
        isObstructionOnRoof = true;
    }
    const { point, object } = interInfo, { rotX, rotY, houseId, roofFaceId } = object as RoofMesh;
    if (selectType === 'obst') {
        virObs.rotation.y = self.roofRotY || 0;
        virObs.children[0].rotation.y = rotY ?? roofMeshArr[0].rotY;
        virObs.children[0].children[0].rotation.x = rotX ?? 0;
        virObs.position.set(point.x, point.y, point.z);
        const virMesh = virObs.children[0].children[0].children[0];
        if (isObstructionOnRoof) {
            virMesh.rotX = rotX;
            virMesh.rotY = rotY;
        }
        virMesh.isObstructionOnRoof = isObstructionOnRoof;
        virMesh.roofId = virObs.roofId = roofFaceId;
        virMesh.roofPos = { ...point };
        SetObstPosition(virObs, interInfo, self.roofRotY, self.roofs[0].faces, self.houseTransPos, isObstructionOnRoof);
    } else if (selectType === 'tree') {
        virTree.position.set(point.x, point.y, point.z);
    }
}

export const resetVirObsToDefault = (self: Viewer) => {
    if (!self.virObs) return;
    const virMesh: any = self.virObs.children[0].children[0].children[0];
    const virFrame: any = self.virObs.children[0].children[0].children[1];
    const { shapeNum, sides } = virMesh;
    virMesh.geometry = GetObstTypeGeo(shapeNum, sides);
    virFrame.geometry = GetObstTypeGeo(shapeNum, sides);
    virMesh.scale.set(defaultObsW, 1, defaultObsW);
    virFrame.scale.set(defaultObsW, 1, defaultObsW);
    virMesh.shapeNum = shapeNum;
    virMesh.sides = 6;
    virMesh.width = defaultObsW;
    virMesh.height = defaultObsW;
    virMesh.length = defaultObsW;
    virMesh.radius = defaultObsW;
    virMesh.diameter = defaultObsW;
    virMesh.verFloor = false;
}

export function SetCloneVirObj(self: Viewer, nodes?: any[], parallel?: boolean) {
    const obstId = GetIdStr();
    const { virModule, virObs, obstGroup, meshArr, virTree, treeGroup, boundGroup, moduleMeshArr } = self;
    if (!virObs) return;
    if (virObs.visible) {
        const cloneObs = virObs.clone(), verPosArr = GetVerPosArr(cloneObs);
        if (!nodes) {
            const overFlag = CheckOverObstArr(obstGroup, moduleMeshArr, virObs.roofId, verPosArr);
            if (overFlag) return;
        }
        const outGroup = cloneObs.children[0], inGroup = outGroup.children[0], obsMesh = inGroup.children[0] as ObstructionMesh,
            obsFrame = inGroup.children[1];
        const virMesh = virObs.children[0].children[0].children[0] as ObstructionMesh, { rotX, rotY } = virMesh;
        if (virMesh.shapeNum === 4) {
            obsMesh.scale.set(1, 1, 1)
            obsFrame.scale.set(1, 1, 1)
            cloneObs.position.set(0, 0, 0)
            obsMesh.position.y = 0;
        }
        obsFrame.position.y = obsMesh.position.y;
        obsMesh.name = 'obstMesh'
        obsMesh.material = new THREE.MeshStandardMaterial({ color: 0xD3D3D3 });

        //add edge geometry border to obstMesh
        const edgeGeo = new THREE.EdgesGeometry(obsMesh.geometry);
        const edgeMat = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 2 });
        const edgeMesh = new THREE.LineSegments(edgeGeo, edgeMat);
        obsMesh.add(edgeMesh);
        cloneObs.isDefaultObstruction = obsMesh.isDefaultObstruction = virObs.isDefaultObstruction;
        cloneObs.isObtsructionOnGround = !virMesh.isObstructionOnRoof;
        cloneObs.userUpdatedDefaultDimensions = virObs.userUpdatedDefaultDimensions;
        obsMesh.isPolygonWithFourNodes = virMesh.isPolygonWithFourNodes;
        obsMesh.meshType = 'obst';
        obsMesh.castShadow = true;
        meshArr.push(obsMesh);
        cloneObs.obstId = obsMesh.obstId = obstId;
        cloneObs.roofId = obsMesh.roofId = virObs.roofId;
        const boundMesh = boundGroup.children[0] as any;
        const boundOriPos = boundMesh.oriPos, boundNewPos = boundMesh.position, obsPos = cloneObs.position;
        const boundDelta = { x: boundNewPos.x - boundOriPos.x, z: boundNewPos.z - boundOriPos.z };
        cloneObs.oriPos = {
            x: obsPos.x - boundDelta.x, y: obsPos.y, z: obsPos.z - boundDelta.z
        };
        let verFloor = cloneObs.verFloor = (nodes && parallel) || false;
        if (!virMesh.isObstructionOnRoof) {
            verFloor = true;
            cloneObs.verFloor = true;
        }
        inGroup.rotation.set(verFloor ? 0 : rotX, 0, 0);
        outGroup.rotation.set(0, rotY, 0);
        cloneObs.posY = cloneObs.position.y;
        cloneObs.rotation.y = self.roofRotY || 0;
        obsMesh.verPosArr = GetVerPosArr(cloneObs);
        ['rotX', 'rotY', 'sides', 'radius', 'diameter', 'width', 'length', 'height', 'shapeNum', 'roofId', 'roofPos'].forEach(key => {
            obsMesh[key] = virMesh[key];
        });
        obsMesh.verFloor = verFloor;
        obsMesh.sclY = obsMesh.scale.y;
        SetMeshOBB(obsMesh);
        obstGroup.add(cloneObs);
        obsMesh.worldPos = new THREE.Vector3(cloneObs.position.x, cloneObs.position.y, cloneObs.position.z);
        SetOutsideObstPos(cloneObs, cloneObs.position);
        if (!nodes) self.addStep(EditAction.ObstCreate, cloneObs); else if (obsMesh.shapeNum === 1) obsMesh.nodes = [...nodes];
        self.refreshShade();
    } else if (virTree?.visible) {
        const cloneTree = virTree.clone(), { crownScl } = virTree.children[0] as any;
        cloneTree.obstId = obstId;
        cloneTree.totalH = virTree.totalH;
        cloneTree.children.forEach((child: any, idx: number) => {
            if (idx === 0) child.crownScl = crownScl; // Crown Mesh
            const { color, map, transparent } = child.material, hexCol = color.getHex();
            child.mapSrc = map;
            child.material = new THREE.MeshStandardMaterial({ color: hexCol, map, transparent });
            if (idx === 2) { // Wrapper Mesh
                child.material.wireframe = true;
                child.material.transparent = true;
                child.visible = false;
                child.worldPos = { x: cloneTree.position.x, z: cloneTree.position.z };
            } else child.oriColor = hexCol; // Trunk Mesh
            child.obstId = obstId;
            child.meshType = 'tree';
            child.castShadow = true;
            meshArr.push(child);
        });
        CheckIsTreeCollidingWithRoof([cloneTree]);
        cloneTree.select = false;
        removeSelectedTree(cloneTree)
        let treeArr = [...self.treeGroup.children];
        const treeId = treeArr.length === 0 ? 'A' : treeArr.length < 26 ? String.fromCharCode(65 + treeArr.length) : String.fromCharCode(65 + Math.floor(treeArr.length / 26) - 1) + String.fromCharCode(65 + treeArr.length % 26);
        cloneTree.userData.treeId = treeId;
        treeGroup.add(cloneTree);
        self.addStep(EditAction.TreeCreate, cloneTree);
        self.refreshShade();
    } else if (virModule.visible) {
        CreateModule(self);
        siteCalculationState.needsUpdate(MODULE_GRAPH);
    }
}

export function SetCopyVirObj(self: Viewer) {
    const selTree = self.treeGroup.children.find((tree: any) => tree.select === true);
    const selObst: any = self.obstGroup.children.find((obst: any) => obst.obstId === self.obstId);
    if (selTree) {
        const crownMesh = selTree.children[0], { crownScl }: any = crownMesh, trunkMesh = selTree.children[1],
            wrapperMesh = selTree.children[2];
        const height = wrapperMesh.scale.y, crown_radius = crownMesh.scale.x / crownScl.x,
            crown_height = crownMesh.scale.y / crownScl.y, trunk_radius = trunkMesh.scale.x;
        SetTreeSize(self, self.virTree, { height, crown_radius, crown_height, trunk_radius });
    }
    if (selObst) {
        if (!self.virObs) return;
        const selMesh = selObst.children[0].children[0].children[0], { shapeNum, sides, scale }: any = selMesh;
        const virMesh: any = self.virObs.children[0].children[0].children[0];
        virMesh.geometry = GetObstTypeGeo(shapeNum, sides);
        const virFrame: any = self.virObs.children[0].children[0].children[1];
        virFrame.geometry = GetObstTypeGeo(shapeNum, sides);
        if (selObst.isDefaultObstruction) {
            virMesh.scale.set(scale.z, scale.y, scale.x);
            virFrame.scale.set(scale.z, scale.y, scale.x);
            virMesh.isClonedFromDefault = true;
            virMesh.width = scale.z;
            virMesh.length = scale.x;
        } else {
            virMesh.scale.set(scale.x, scale.y, scale.z);
            virFrame.scale.set(scale.x, scale.y, scale.z);
            virMesh.width = scale.x;
            virMesh.length = scale.z;
        }
        virMesh.shapeNum = shapeNum;
        virMesh.sides = sides;
        virMesh.height = scale.y;
        virMesh.radius = scale.x;
        virMesh.diameter = scale.x;
    }
}